(function (scope, factory) {
  'use strict';
  var avatar = factory(scope);
  scope.RwadyAvatar = avatar;
  if (typeof module !== 'undefined' && module.exports) module.exports = avatar;
}(typeof window !== 'undefined' ? window : globalThis, function (scope) {
  'use strict';

  var ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  var OUTPUT_SIZE = 512;
  var MAX_OUTPUT_BYTES = 500 * 1024;
  var MAX_SOURCE_BYTES = 20 * 1024 * 1024;
  var objectUrl = '';
  var state = {
    authenticated: false,
    loading: true,
    imageUrl: '',
    fullName: '',
    firstName: '',
    initials: ''
  };

  function splitName(value) {
    return String(value || '').trim().split(/\s+/).filter(Boolean);
  }

  function firstCharacter(value) {
    var text = String(value || '').trim();
    if (!text) return '';
    if (typeof Intl !== 'undefined' && Intl.Segmenter) {
      return Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text))[0].segment;
    }
    return Array.from(text)[0] || '';
  }

  function initials(value) {
    var parts = splitName(value);
    if (!parts.length) return '';
    var result = firstCharacter(parts[0]);
    if (parts.length > 1) result += firstCharacter(parts[parts.length - 1]);
    return result.toLocaleUpperCase();
  }

  function profileName(profile) {
    var source = profile || {};
    return String(source.full_name || [source.first_name, source.last_name].filter(Boolean).join(' ') || '').trim();
  }

  function snapshot() {
    return Object.assign({}, state);
  }

  function emit(reason) {
    if (!scope.document || typeof scope.CustomEvent !== 'function') return;
    scope.document.dispatchEvent(new scope.CustomEvent('rwady:account-avatar-updated', {
      detail: Object.assign({ reason: reason || 'update' }, snapshot())
    }));
  }

  function revokeObjectUrl() {
    if (!objectUrl) return;
    if (scope.URL && typeof scope.URL.revokeObjectURL === 'function') scope.URL.revokeObjectURL(objectUrl);
    objectUrl = '';
  }

  function setProfile(profile, options) {
    var source = profile || {};
    var fullName = profileName(source);
    state.authenticated = true;
    state.loading = Boolean(options && options.loading);
    state.fullName = fullName;
    state.firstName = String(source.first_name || splitName(fullName)[0] || '');
    state.initials = initials(fullName);
    emit('profile');
    return snapshot();
  }

  function setBlob(blob, profile) {
    if (!blob || !scope.URL || typeof scope.URL.createObjectURL !== 'function') throw new Error('AVATAR_BLOB_REQUIRED');
    validateFile(blob);
    revokeObjectUrl();
    objectUrl = scope.URL.createObjectURL(blob);
    state.imageUrl = objectUrl;
    if (profile) setProfile(profile, { loading: false });
    state.authenticated = true;
    state.loading = false;
    emit('image');
    return snapshot();
  }

  function clearImage(profile) {
    revokeObjectUrl();
    state.imageUrl = '';
    if (profile) setProfile(profile, { loading: false });
    state.loading = false;
    emit('image-removed');
    return snapshot();
  }

  function logout() {
    revokeObjectUrl();
    state = {
      authenticated: false,
      loading: false,
      imageUrl: '',
      fullName: '',
      firstName: '',
      initials: ''
    };
    emit('logout');
    return snapshot();
  }

  function validateFile(file) {
    if (!file || ALLOWED_TYPES.indexOf(String(file.type || '').toLowerCase()) === -1) {
      var typeError = new Error('AVATAR_UNSUPPORTED_TYPE');
      typeError.code = 'AVATAR_UNSUPPORTED_TYPE';
      throw typeError;
    }
    if (Number(file.size) > MAX_SOURCE_BYTES) {
      var sizeError = new Error('AVATAR_SOURCE_TOO_LARGE');
      sizeError.code = 'AVATAR_SOURCE_TOO_LARGE';
      throw sizeError;
    }
    return true;
  }

  function decodeImage(file, environment) {
    validateFile(file);
    var env = environment || scope;
    if (typeof env.createImageBitmap === 'function') {
      return env.createImageBitmap(file, { imageOrientation: 'from-image' });
    }
    return new Promise(function (resolve, reject) {
      var url = env.URL.createObjectURL(file);
      var image = new env.Image();
      image.onload = function () {
        env.URL.revokeObjectURL(url);
        resolve(image);
      };
      image.onerror = function () {
        env.URL.revokeObjectURL(url);
        var error = new Error('AVATAR_DECODE_FAILED');
        error.code = 'AVATAR_DECODE_FAILED';
        reject(error);
      };
      image.src = url;
    });
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function drawSquare(canvas, image, crop) {
    var context = canvas.getContext('2d', { alpha: false });
    var width = Number(image.naturalWidth || image.width);
    var height = Number(image.naturalHeight || image.height);
    var zoom = clamp(Number(crop && crop.zoom) || 1, 1, 3);
    var baseSide = Math.min(width, height) / zoom;
    var maxOffsetX = Math.max(0, (width - baseSide) / 2);
    var maxOffsetY = Math.max(0, (height - baseSide) / 2);
    var offsetX = clamp(Number(crop && crop.offsetX) || 0, -maxOffsetX, maxOffsetX);
    var offsetY = clamp(Number(crop && crop.offsetY) || 0, -maxOffsetY, maxOffsetY);
    var sourceX = clamp((width - baseSide) / 2 + offsetX, 0, width - baseSide);
    var sourceY = clamp((height - baseSide) / 2 + offsetY, 0, height - baseSide);
    context.save();
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, sourceX, sourceY, baseSide, baseSide, 0, 0, canvas.width, canvas.height);
    context.restore();
    return { sourceX: sourceX, sourceY: sourceY, side: baseSide };
  }

  function canvasBlob(canvas, type, quality) {
    return new Promise(function (resolve) {
      canvas.toBlob(resolve, type, quality);
    });
  }

  async function encodeCanvas(canvas) {
    var preferredType = 'image/webp';
    var quality = 0.9;
    var blob = null;
    while (quality >= 0.48) {
      blob = await canvasBlob(canvas, preferredType, quality);
      if (!blob || blob.type !== preferredType) {
        preferredType = 'image/jpeg';
        blob = await canvasBlob(canvas, preferredType, quality);
      }
      if (blob && blob.size <= MAX_OUTPUT_BYTES) break;
      quality -= 0.08;
    }
    if (!blob || blob.size > MAX_OUTPUT_BYTES) {
      var error = new Error('AVATAR_OUTPUT_TOO_LARGE');
      error.code = 'AVATAR_OUTPUT_TOO_LARGE';
      throw error;
    }
    return {
      blob: blob,
      type: blob.type,
      filename: 'rwady-avatar.' + (blob.type === 'image/webp' ? 'webp' : 'jpg'),
      width: OUTPUT_SIZE,
      height: OUTPUT_SIZE,
      bytes: blob.size
    };
  }

  async function processImage(file, crop, environment) {
    validateFile(file);
    var env = environment || scope;
    var image = await decodeImage(file, env);
    var canvas = env.document.createElement('canvas');
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    drawSquare(canvas, image, crop || {});
    var output = await encodeCanvas(canvas);
    if (image && typeof image.close === 'function') image.close();
    return output;
  }

  if (scope.addEventListener) {
    scope.addEventListener('pagehide', revokeObjectUrl);
  }

  return {
    ALLOWED_TYPES: ALLOWED_TYPES.slice(),
    OUTPUT_SIZE: OUTPUT_SIZE,
    MAX_OUTPUT_BYTES: MAX_OUTPUT_BYTES,
    MAX_SOURCE_BYTES: MAX_SOURCE_BYTES,
    initials: initials,
    profileName: profileName,
    validateFile: validateFile,
    decodeImage: decodeImage,
    drawSquare: drawSquare,
    encodeCanvas: encodeCanvas,
    processImage: processImage,
    snapshot: snapshot,
    setProfile: setProfile,
    setBlob: setBlob,
    clearImage: clearImage,
    logout: logout,
    revokeObjectUrl: revokeObjectUrl
  };
}));
